import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { selectAccountSlug } from '@/redux/session/selectors';
import { trackEvent } from '@/segment/segment';
import { EventName } from '@/shared/types/segment';

import { useCampaigns } from '../../hooks/useCampaigns';

interface StopCampaignModalProps {
  modalCampaignId: string;
  setModalCampaignId: (modalCampaignId: string | null) => void;
  filter?: string;
}

export const StopCampaignModal = ({
  modalCampaignId,
  setModalCampaignId,
  filter,
}: StopCampaignModalProps) => {
  const slug = useSelector(selectAccountSlug);
  const { campaign, updateCampaignAsync, updateStatus } = useCampaigns(modalCampaignId, filter);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isStopping = updateStatus === 'pending';

  const closeModal = () => {
    setModalCampaignId(null);
  };

  const handleStop = async () => {
    trackEvent(EventName.ConfirmStopCampaign, { campaign_id: modalCampaignId });
    try {
      await updateCampaignAsync({ campaign_id: modalCampaignId, status: 'canceled' });
      toast.success(t('campaigns.stopped', { 0: campaign?.name }));
      closeModal();
      navigate(`/${slug}/campaigns`, { replace: true });
    } catch {
      // Error toast handled by useCampaigns onError
    }
  };

  return (
    <Dialog open={!!modalCampaignId} onOpenChange={closeModal}>
      <DialogContent className="gap-6 sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('campaigns.stop_campaign')}</DialogTitle>
          <DialogDescription>{t('campaigns.stop_description')}</DialogDescription>
        </DialogHeader>

        <div className="flex justify-end gap-2">
          <Button variant="ghost-neutral" onClick={closeModal}>
            {t('common.back_to_safety')}
          </Button>
          <Button onClick={handleStop} isLoading={isStopping}>
            {t('campaigns.stop')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
